import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../services/apiV2';
import { AuthSession } from '../../types';
import { MentionInput } from '../mention-input';
import { MentionText } from '../mentions';

// ─────────────────────────────────────────────────────────────────────────────
// Página de PROPOSTA VIVA (co-criação) + LANÇAMENTO (contagem regressiva).
// Tela autônoma (carrega via api.getClubProposal). Discussão em TEMPO REAL via
// SSE (api.openProposalStream) — comentário/edição/voto aparecem na hora.
// Quando o clube ativa, chama onOpenClub. Tema escuro warm (design Forjamente).
// ─────────────────────────────────────────────────────────────────────────────

const C = {
  bg: '#16110d', ember: '#e8702a', emberSoft: '#e8a472', ink: '#f2ebdd',
  ink2: '#c5bbab', muted: '#9e9485', faint: '#857a6b',
  line: 'rgba(255,255,255,0.08)', card: 'rgba(255,255,255,0.035)', card2: 'rgba(255,255,255,0.06)',
};
const EMBER_RGB = '232,112,42';

function timeAgo(iso?: string | null): string {
  if (!iso) return '';
  const t = Date.parse(iso); if (!Number.isFinite(t)) return '';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return 'agora';
  const m = Math.floor(s / 60); if (m < 60) return `há ${m} min`;
  const h = Math.floor(m / 60); if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24); if (d < 7) return `há ${d} ${d === 1 ? 'dia' : 'dias'}`;
  try { return new Date(t).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }); } catch { return ''; }
}
function fmtStart(iso?: string | null): string {
  if (!iso) return 'a definir';
  try {
    const d = new Date(iso);
    const dia = d.toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric', month: 'short' });
    const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return `${dia} · ${hora}`;
  } catch { return 'a definir'; }
}
function hueFrom(seed: string): number { let h = 0; for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360; return h; }

const Avatar: React.FC<{ name?: string | null; url?: string | null; id?: string | null; size?: number; onClick?: () => void; ring?: boolean }> = ({ name, url, id, size = 32, onClick, ring }) => {
  const initial = (name || '?').trim().charAt(0).toUpperCase() || '?';
  const hue = hueFrom(id || name || '?');
  return (
    <div onClick={onClick} style={{
      flex: 'none', width: size, height: size, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.42, fontWeight: 600, color: '#fff', overflow: 'hidden', cursor: onClick ? 'pointer' : 'default',
      background: `linear-gradient(135deg, hsl(${hue},42%,42%), hsl(${hue},48%,26%))`, border: ring ? `2px solid ${C.bg}` : undefined,
    }}>{url ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initial}</div>
  );
};

const MonoLabel: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color }) => (
  <div style={{ fontFamily: "'Geist Mono', monospace", fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase', color: color || C.faint }}>{children}</div>
);

const HourglassIcon: React.FC<{ size?: number; pulse?: boolean }> = ({ size = 30, pulse }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={pulse ? { animation: 'fjPulse 3s ease-in-out infinite' } : undefined}><path d="M6 2h12M6 22h12M6 2c0 5 6 6 6 10 0-4 6-5 6-10M6 22c0-5 6-6 6-10 0 4 6 5 6 10" stroke={C.ember} strokeWidth="1.4" strokeLinejoin="round" /></svg>
);

type CItem = { id: string; parentId: string | null; author: any; authorId: string; text: string | null; deleted: boolean; createdAt: string; editedAt: string | null; likes: number; likedByMe: boolean; isMine?: boolean; canModerate?: boolean; canEdit?: boolean; replies?: CItem[] };

export const ProposalPage: React.FC<{
  session: AuthSession | null;
  clubId: string;
  currentUserId?: string;
  myName?: string;
  myAvatarUrl?: string | null;
  onBack: () => void;
  onOpenProfile?: (uid: string) => void;
  onOpenClub?: (clubId: string) => void;
  onEditProposal?: (clubId: string, data: any) => void;
}> = ({ session, clubId, currentUserId, myName, myAvatarUrl, onBack, onOpenProfile, onOpenClub, onEditProposal }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState<CItem[]>([]);
  const [text, setText] = useState('');
  const [replyTo, setReplyTo] = useState<{ id: string; name: string } | null>(null);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [, forceTick] = useState(0); // p/ a contagem regressiva
  const inputRef = useRef<HTMLInputElement>(null);
  const discussionRef = useRef<HTMLDivElement>(null);
  const liveRef = useRef(false);

  const reload = async () => {
    try { const d = await api.getClubProposal(session, clubId); setData(d); if (d?.club?.status === 'ativo') { onOpenClub?.(clubId); } }
    catch { /* sem acesso/erro */ }
    finally { setLoading(false); }
  };
  const reloadComments = async () => { try { const r = await api.listProposalComments(session, clubId); setComments((r.comments as any) || []); } catch {} };

  // mount: carrega dados + comentários + abre SSE; fallback polling se SSE cair
  useEffect(() => {
    let alive = true; let pollId: any = null;
    setLoading(true);
    reload(); reloadComments();
    const cancel = api.openProposalStream(session, clubId, (ev, payload) => {
      liveRef.current = true;
      if (ev === 'comment') {
        setComments(prev => addComment(prev, payload));
      } else if (ev === 'comment:edit') {
        setComments(prev => mapComments(prev, c => c.id === payload.id ? { ...c, text: payload.text, editedAt: new Date().toISOString() } : c));
      } else if (ev === 'comment:delete') {
        setComments(prev => mapComments(prev, c => c.id === payload.id ? { ...c, deleted: true, text: null } : c));
      } else if (ev === 'comment:like') {
        setComments(prev => mapComments(prev, c => c.id === payload.id ? { ...c, likes: payload.count } : c));
      } else if (ev === 'roster' || ev === 'proposal' || ev === 'launched' || ev === 'log') {
        reload();
      }
    });
    // fallback: se em 4s o SSE não deu 'hello', faz polling leve
    const fb = setTimeout(() => { if (!liveRef.current) { pollId = setInterval(() => { if (alive) { reload(); reloadComments(); } }, 8000); } }, 4000);
    // tick da contagem regressiva
    const tick = setInterval(() => { if (alive) forceTick(x => x + 1); }, 1000);
    return () => { alive = false; cancel(); clearTimeout(fb); if (pollId) clearInterval(pollId); clearInterval(tick); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId, session]);

  if (loading && !data) return <Shell onBack={onBack}><div style={{ padding: '70px 24px', textAlign: 'center', color: C.muted, fontSize: 14 }}>Carregando proposta…</div></Shell>;
  if (!data) return <Shell onBack={onBack}><div style={{ padding: '70px 24px', textAlign: 'center' }}><div style={{ fontFamily: "'Instrument Serif',serif", fontSize: 22, color: C.ink, marginBottom: 8 }}>Proposta indisponível</div><div style={{ fontSize: 14, color: C.muted }}>Você pode não ter sido convidado, ou ela foi removida.</div></div></Shell>;

  const club = data.club; const owner = data.owner || {};
  const isOwner = !!data.isOwner;
  const isLaunch = club.status === 'lancamento';
  const ownerFirst = String(owner.name || 'Alguém').split(' ')[0];
  const startIso = isLaunch ? club.startAt : club.proposedStartAt;

  // ── ações ──
  const doInterest = async () => { setBusy(true); try { const r = await api.proposalInterest(session, clubId); setData((d: any) => ({ ...d, myStatus: r.myStatus })); reload(); } catch (e: any) { window.alert(e?.message || 'Erro'); } finally { setBusy(false); } };
  const doDecline = async () => { setBusy(true); try { await api.proposalDecline(session, clubId); setData((d: any) => ({ ...d, myStatus: 'recusou' })); reload(); } catch (e: any) { window.alert(e?.message || 'Erro'); } finally { setBusy(false); } };
  const doReserve = async () => { setBusy(true); try { await api.proposalReserve(session, clubId); setData((d: any) => ({ ...d, myStatus: 'reservado' })); reload(); } catch (e: any) { window.alert(e?.message || 'Erro'); } finally { setBusy(false); } };
  const doLeave = async () => { if (!window.confirm('Retirar sua participação?')) return; setBusy(true); try { await api.proposalLeave(session, clubId); setData((d: any) => ({ ...d, myStatus: 'convidado' })); reload(); } catch (e: any) { window.alert(e?.message || 'Erro'); } finally { setBusy(false); } };
  const doLaunch = async () => {
    const when = club.proposedStartAt ? fmtStart(club.proposedStartAt) : 'imediatamente';
    if (!window.confirm(`Aprovar e lançar o clube? Começa ${when}. A partir daqui a data trava e os interessados ganham vaga.`)) return;
    setBusy(true);
    try { const r = await api.proposalLaunch(session, clubId, { startAt: club.proposedStartAt || undefined }); if (r.status === 'ativo') { onOpenClub?.(clubId); } else { reload(); } }
    catch (e: any) { window.alert(e?.message || 'Erro ao lançar'); } finally { setBusy(false); }
  };

  // ── discussão ──
  const submit = async () => {
    const t = text.trim(); if (!t || sending) return; setSending(true);
    try { await api.addProposalComment(session, clubId, t, replyTo?.id || null); setText(''); setReplyTo(null); if (!liveRef.current) reloadComments(); }
    catch (e: any) { window.alert(e?.message || 'Não foi possível comentar.'); }
    finally { setSending(false); }
  };
  const toggleLike = async (c: CItem) => {
    setComments(prev => mapComments(prev, x => x.id === c.id ? { ...x, likedByMe: !x.likedByMe, likes: x.likes + (x.likedByMe ? -1 : 1) } : x));
    try { await api.likeProposalComment(session, clubId, c.id); } catch { setComments(prev => mapComments(prev, x => x.id === c.id ? { ...x, likedByMe: !x.likedByMe, likes: x.likes + (x.likedByMe ? 1 : -1) } : x)); }
  };
  const saveEdit = async () => { if (!editing) return; const t = editing.text.trim(); if (!t) return; try { await api.editProposalComment(session, clubId, editing.id, t); setEditing(null); if (!liveRef.current) reloadComments(); } catch (e: any) { window.alert(e?.message || 'Erro'); } };
  const remove = async (c: CItem) => { if (!window.confirm('Excluir comentário?')) return; try { await api.deleteProposalComment(session, clubId, c.id); if (!liveRef.current) reloadComments(); } catch (e: any) { window.alert(e?.message || 'Erro'); } };
  const startReply = (c: CItem) => { setReplyTo({ id: c.id, name: c.author?.name || 'comentário' }); setTimeout(() => inputRef.current?.focus(), 30); };
  const scrollToDiscussion = () => discussionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const totalComments = comments.reduce((n, c) => n + 1 + (c.replies?.length || 0), 0);
  const cd = countdown(startIso);

  return (
    <Shell onBack={onBack} badge={isLaunch ? 'LANÇAMENTO' : undefined}>
      <div style={{ padding: '6px 22px calc(120px + env(safe-area-inset-bottom, 0px))' }}>
        {/* ── identidade ── */}
        <div style={{ textAlign: 'center', paddingTop: 4 }}>
          {isLaunch && <HourglassIcon size={36} pulse />}
          <div style={{ fontFamily: "'Instrument Serif',serif", fontSize: isLaunch ? 38 : 32, lineHeight: 1, color: C.ink, marginTop: isLaunch ? 12 : 4 }}>{club.name}</div>
          <button onClick={() => owner.id && onOpenProfile?.(owner.id)} style={{ background: 'none', border: 'none', cursor: owner.id ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 9, color: C.muted, fontSize: 13 }}>
            <Avatar name={owner.name} url={owner.avatarUrl} id={owner.id} size={22} /> por {owner.name} · {data.books?.length || 0} {(data.books?.length || 0) === 1 ? 'livro' : 'livros'}
          </button>
        </div>

        {/* ── contagem (lançamento) ou "começa" (proposta) ── */}
        {isLaunch ? (
          <>
            <div style={{ marginTop: 22, display: 'flex', gap: 9 }}>
              {[['dias', cd.d], ['horas', cd.h], ['min', cd.m]].map(([lbl, v]) => (
                <div key={lbl as string} style={{ flex: 1, background: 'rgba(0,0,0,0.3)', border: `1px solid rgba(${EMBER_RGB},0.25)`, borderRadius: 16, padding: '16px 0', textAlign: 'center' }}>
                  <div style={{ fontFamily: "'Instrument Serif',serif", fontSize: 36, lineHeight: 1, color: C.ember }}>{String(v).padStart(2, '0')}</div>
                  <div style={{ marginTop: 5 }}><MonoLabel>{lbl}</MonoLabel></div>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center', fontSize: 12.5, color: C.muted, marginTop: 12 }}>Começa <span style={{ color: C.ink }}>{fmtStart(startIso)}</span></div>
          </>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 9, margin: '18px 0 6px' }}>
            {(data.books || []).slice(0, 4).map((b: any, i: number) => (
              <BookSpine key={b.id || i} title={b.title} hue={b.coverHue} url={b.coverUrl} />
            ))}
          </div>
        )}

        {/* ── objetivo ── */}
        {club.objective && (
          <div style={{ borderLeft: `2px solid ${C.ember}`, padding: '2px 0 2px 15px', margin: '22px 0 18px' }}>
            <MonoLabel color={C.emberSoft}>O objetivo</MonoLabel>
            <div style={{ fontFamily: "'Instrument Serif',serif", fontStyle: 'italic', fontSize: 18, lineHeight: 1.42, color: C.ink, marginTop: 7 }}>{club.objective}</div>
          </div>
        )}

        {/* ── regras ── */}
        {club.rulesText && (
          <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: '14px 15px', marginBottom: 18 }}>
            <MonoLabel>Regras</MonoLabel>
            <div style={{ marginTop: 9, fontSize: 13, lineHeight: 1.5, color: C.muted, whiteSpace: 'pre-wrap' }}>{club.rulesText}</div>
          </div>
        )}

        {/* ── quem reservou/curtiu ── */}
        {(data.people || []).length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 11 }}>
              <MonoLabel>{isLaunch ? 'Já reservaram' : 'Curtiram a ideia'}</MonoLabel>
              <span style={{ fontSize: 12, color: C.emberSoft, fontWeight: 600 }}>{isLaunch ? data.counts?.reserved : data.counts?.interested} {((isLaunch ? data.counts?.reserved : data.counts?.interested) === 1) ? 'pessoa' : 'pessoas'}</span>
            </div>
            <div style={{ display: 'flex' }}>
              {(data.people || []).slice(0, 6).map((p: any, i: number) => (
                <div key={p.id} style={{ marginLeft: i ? -10 : 0 }}><Avatar name={p.name} url={p.avatarUrl} id={p.id} size={32} ring onClick={() => p.id && onOpenProfile?.(p.id)} /></div>
              ))}
            </div>
          </div>
        )}

        {/* ── log de mudanças (proposta) ── */}
        {!isLaunch && (data.events || []).length > 0 && (
          <div style={{ background: 'rgba(0,0,0,0.18)', border: `1px solid ${C.line}`, borderRadius: 14, padding: '11px 13px', marginBottom: 18 }}>
            {(data.events || []).slice(0, 4).map((e: any, i: number) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11.5, color: C.muted, padding: '3px 0' }}>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.detail || e.type}</span>
                <span style={{ flex: 'none', color: C.faint }}>{timeAgo(e.at)}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── AÇÕES ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginTop: 8 }}>
          {isOwner ? (
            <>
              {!isLaunch && (
                <button onClick={doLaunch} disabled={busy} style={primaryBtn}>🚀 Aprovar e lançar</button>
              )}
              <button onClick={() => onEditProposal?.(clubId, data)} style={ghostBtn}>✏️ Editar a proposta</button>
            </>
          ) : isLaunch ? (
            data.myStatus === 'reservado' ? (
              <div style={{ ...primaryBtn, opacity: 0.95, cursor: 'default', background: `rgba(${EMBER_RGB},0.15)`, color: C.emberSoft, border: `1px solid rgba(${EMBER_RGB},0.4)` }}>✓ Vaga reservada — avisaremos quando começar</div>
            ) : (
              <button onClick={doReserve} disabled={busy} style={primaryBtn}>Reservar vaga</button>
            )
          ) : (
            <>
              <button onClick={doInterest} disabled={busy} style={data.myStatus === 'interessado' ? { ...primaryBtn, background: `rgba(${EMBER_RGB},0.15)`, color: C.emberSoft, border: `1px solid rgba(${EMBER_RGB},0.4)` } : primaryBtn}>
                {data.myStatus === 'interessado' ? '✓ Você quer participar' : 'Gostei! Quero participar'}
              </button>
              <button onClick={scrollToDiscussion} style={ghostBtn}>Propor mudanças</button>
              {data.myStatus !== 'recusou' && <button onClick={doDecline} style={{ ...ghostBtn, border: 'none', color: C.faint }}>Agora não</button>}
            </>
          )}
        </div>

        <div style={{ height: 1, background: C.line, margin: '24px 0' }} />

        {/* ── DISCUSSÃO (tempo real) ── */}
        <div ref={discussionRef}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <MonoLabel>Discussão</MonoLabel>
            {totalComments > 0 && <span style={{ fontFamily: "'Geist Mono',monospace", fontSize: 10.5, color: C.faint }}>· {totalComments}</span>}
            {liveRef.current && <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, color: C.emberSoft }}><span style={{ width: 6, height: 6, borderRadius: '50%', background: C.ember, animation: 'fjPulse 2s infinite' }} /> ao vivo</span>}
          </div>

          {/* composer */}
          <div style={{ marginBottom: 18 }}>
            {replyTo && <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 12, color: C.muted }}>Respondendo <strong style={{ color: C.ink2 }}>{replyTo.name}</strong><button onClick={() => setReplyTo(null)} style={{ background: 'none', border: 'none', color: C.faint, cursor: 'pointer' }}>✕</button></div>}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <Avatar name={myName} url={myAvatarUrl} size={32} />
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.card2}`, borderRadius: 99, padding: '6px 6px 6px 15px' }}>
                <MentionInput inputRef={inputRef} value={text} onChange={setText} onEnter={submit} dropdownPosition="above"
                  placeholder="Comente ou proponha uma mudança…" wrapStyle={{ flex: 1, minWidth: 0 }} inputStyle={{ width: '100%', background: 'none', border: 'none', outline: 'none', color: C.ink, fontSize: 13 }} />
                <button onClick={submit} disabled={sending || !text.trim()} style={{ flex: 'none', width: 30, height: 30, borderRadius: '50%', background: text.trim() ? C.ember : C.card2, border: 'none', cursor: text.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1a120c" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4z" /></svg>
                </button>
              </div>
            </div>
          </div>

          {totalComments === 0 ? (
            <div style={{ color: C.muted, fontSize: 13.5, textAlign: 'center', padding: '14px 0', fontStyle: 'italic', fontFamily: "'Instrument Serif',serif" }}>Ninguém comentou ainda. Puxe a primeira ideia.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {comments.map(c => <CommentNode key={c.id} c={c} depth={0} myId={currentUserId} isOwner={isOwner} onLike={toggleLike} onReply={startReply} onEdit={(cc: CItem) => setEditing({ id: cc.id, text: cc.text || '' })} onDelete={remove} onOpenProfile={onOpenProfile} editing={editing} setEditing={setEditing} saveEdit={saveEdit} />)}
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
};

// ── shell (header + fundo) ──
const Shell: React.FC<{ onBack: () => void; badge?: string; children: React.ReactNode }> = ({ onBack, badge, children }) => (
  <div className="scroll" style={{ minHeight: '100%', background: `radial-gradient(130% 55% at 50% -4%, rgba(${EMBER_RGB},0.18), transparent 55%), ${C.bg}`, color: C.ink }}>
    <style>{`@keyframes fjPulse{0%,100%{opacity:.45}50%{opacity:1}}`}</style>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px 6px' }}>
      <button onClick={onBack} style={{ width: 34, height: 34, borderRadius: 11, background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.card2}`, display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
        <svg width="9" height="15" viewBox="0 0 9 15" fill="none"><path d="m7.5 1.5-6 6 6 6" stroke={C.ink} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {badge ? <div style={{ fontFamily: "'Geist Mono',monospace", fontSize: 9, letterSpacing: '0.18em', background: C.ember, color: '#16110d', padding: '5px 10px', borderRadius: 8, fontWeight: 600 }}>{badge}</div>
        : <MonoLabel>Proposta</MonoLabel>}
      <div style={{ width: 34 }} />
    </div>
    {children}
  </div>
);

const BookSpine: React.FC<{ title?: string; hue?: number; url?: string | null }> = ({ title, hue, url }) => (
  <div style={{ width: 54, height: 78, borderRadius: 8, overflow: 'hidden', background: url ? '#000' : `linear-gradient(155deg, hsl(${(hue ?? 22)},45%,32%), hsl(${(hue ?? 22)},50%,16%))`, display: 'flex', alignItems: 'flex-end', padding: url ? 0 : 6, flex: 'none' }}>
    {url ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontFamily: "'Instrument Serif',serif", fontSize: 9, lineHeight: 1, color: C.ink }}>{title}</span>}
  </div>
);

const CommentNode: React.FC<any> = ({ c, depth, myId, isOwner, onLike, onReply, onEdit, onDelete, onOpenProfile, editing, setEditing, saveEdit }) => {
  const isEditing = editing?.id === c.id;
  const mine = c.authorId === myId;
  return (
    <div style={{ display: 'flex', gap: depth ? 10 : 12 }}>
      <Avatar name={c.author?.name} url={c.author?.avatarUrl} id={c.author?.id} size={depth ? 28 : 34} onClick={() => c.author?.id && onOpenProfile?.(c.author.id)} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
          <span onClick={() => c.author?.id && onOpenProfile?.(c.author.id)} style={{ fontSize: depth ? 12.5 : 13.5, fontWeight: 600, color: C.ink, cursor: c.author?.id ? 'pointer' : 'default' }}>{c.author?.name || 'Alguém'}</span>
          <span style={{ fontSize: depth ? 10.5 : 11, color: C.faint }}>{timeAgo(c.createdAt)}{c.editedAt ? ' · editado' : ''}</span>
        </div>
        {c.deleted ? (
          <p style={{ margin: '4px 0 0', fontSize: 13, color: C.faint, fontStyle: 'italic' }}>comentário removido</p>
        ) : isEditing ? (
          <div style={{ marginTop: 6 }}>
            <MentionInput multiline value={editing.text} onChange={(v) => setEditing({ id: c.id, text: v })} rows={2} autoFocus dropdownPosition="below" inputStyle={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.line}`, borderRadius: 10, padding: '8px 10px', color: C.ink, fontSize: 13.5 }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <button onClick={() => setEditing(null)} style={{ padding: '6px 12px', borderRadius: 9, background: C.card2, color: C.ink2, fontSize: 12, border: 'none' }}>Cancelar</button>
              <button onClick={saveEdit} style={{ padding: '6px 12px', borderRadius: 9, background: C.ember, color: '#1a120c', fontSize: 12, fontWeight: 600, border: 'none' }}>Salvar</button>
            </div>
          </div>
        ) : (
          <p style={{ margin: '4px 0 0', fontSize: depth ? 13 : 13.5, lineHeight: 1.5, color: C.ink2 }}><MentionText text={c.text} /></p>
        )}
        {!c.deleted && !isEditing && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
            <button onClick={() => onLike(c)} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, color: c.likedByMe ? C.ember : C.faint }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill={c.likedByMe ? C.ember : 'none'} stroke={c.likedByMe ? C.ember : C.faint} strokeWidth="2"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 1 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z" /></svg>{c.likes > 0 ? c.likes : ''}
            </button>
            {depth === 0 && <button onClick={() => onReply(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, color: C.faint, fontWeight: 500 }}>Responder</button>}
            {mine && <button onClick={() => onEdit(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, color: C.faint }}>Editar</button>}
            {(mine || isOwner) && <button onClick={() => onDelete(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, color: C.faint }}>Excluir</button>}
          </div>
        )}
        {c.replies && c.replies.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
            {c.replies.map((r: CItem) => <CommentNode key={r.id} c={r} depth={depth + 1} myId={myId} isOwner={isOwner} onLike={onLike} onReply={onReply} onEdit={onEdit} onDelete={onDelete} onOpenProfile={onOpenProfile} editing={editing} setEditing={setEditing} saveEdit={saveEdit} />)}
          </div>
        )}
      </div>
    </div>
  );
};

// ── helpers de árvore de comentários ──
function addComment(list: CItem[], item: CItem): CItem[] {
  // dedupe por id (evita render duplo do meu próprio comentário)
  if (existsById(list, item.id)) return list;
  if (!item.parentId) return [...list, { ...item, replies: item.replies || [] }];
  // insere a resposta no pai em QUALQUER profundidade; se o pai ainda não está
  // na lista (corrida via SSE), mantém inalterado — o polling/reload corrige.
  if (!existsById(list, item.parentId)) return list;
  const insert = (nodes: CItem[]): CItem[] => nodes.map(c => {
    if (c.id === item.parentId) return { ...c, replies: [...(c.replies || []), item] };
    if (c.replies && c.replies.length) return { ...c, replies: insert(c.replies) };
    return c;
  });
  return insert(list);
}
function existsById(list: CItem[], id: string): boolean {
  for (const c of list) { if (c.id === id) return true; if (c.replies && existsById(c.replies, id)) return true; }
  return false;
}
function mapComments(list: CItem[], fn: (c: CItem) => CItem): CItem[] {
  return list.map(c => { const u = fn(c); return { ...u, replies: c.replies ? mapComments(c.replies, fn) : c.replies }; });
}
function countdown(iso?: string | null): { d: number; h: number; m: number } {
  if (!iso) return { d: 0, h: 0, m: 0 };
  const diff = Math.max(0, Date.parse(iso) - Date.now());
  return { d: Math.floor(diff / 86400000), h: Math.floor((diff % 86400000) / 3600000), m: Math.floor((diff % 3600000) / 60000) };
}

const primaryBtn: React.CSSProperties = { width: '100%', border: 'none', cursor: 'pointer', borderRadius: 16, padding: 16, background: C.ember, color: '#16110d', fontSize: 15, fontWeight: 600, boxShadow: `0 14px 30px -10px rgba(${EMBER_RGB},0.6)`, textAlign: 'center' };
const ghostBtn: React.CSSProperties = { width: '100%', background: 'transparent', color: C.muted, border: `1px solid ${C.card2}`, borderRadius: 16, padding: 14, fontSize: 14, fontWeight: 500, cursor: 'pointer' };
